import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { tasksService } from '@/services/tasks.service';
import { clientsService } from '@/services/clients.service';
import { projectsService } from '@/services/projects.service';
import type { Priority, Task, Client, Project } from '@/types';

export function EditTaskForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    clientId: '',
    projectId: '',
    priority: 'medium' as Priority,
    deadline: '',
  });
  const [hasDeadline, setHasDeadline] = useState(false);

  useEffect(() => {
    if (id) {
      loadTaskAndData();
    }
  }, [id]);

  const loadTaskAndData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const [taskData, clientsData, projectsData] = await Promise.all([
        tasksService.getTask(id),
        clientsService.getClients(),
        projectsService.getProjects(),
      ]);

      setTask(taskData);
      setClients(clientsData);
      setProjects(projectsData);

      // Set form data from loaded task
      setFormData({
        title: taskData.title,
        description: taskData.description || '',
        clientId: taskData.clientId || '',
        projectId: taskData.projectId || '',
        priority: taskData.priority,
        deadline: taskData.deadline ? taskData.deadline.split('T')[0] : '',
      });

      // Set hasDeadline based on whether task has a deadline
      setHasDeadline(!!taskData.deadline);
    } catch (err: any) {
      console.error('Error loading task:', err);
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload: any = {
        title: formData.title,
        priority: formData.priority,
      };

      // Only include optional fields if they have values
      if (formData.description) payload.description = formData.description;
      if (formData.clientId) payload.clientId = formData.clientId;
      if (formData.projectId) payload.projectId = formData.projectId;
      if (formData.deadline) {
        // Convert YYYY-MM-DD to ISO 8601 date-time format
        payload.deadline = new Date(formData.deadline).toISOString();
      }

      await tasksService.updateTask(id, payload);
      navigate(`/tasks/${id}`);
    } catch (err: any) {
      console.error('Error updating task:', err);
      setError(err.message || 'Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flowky-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading task...</p>
        </div>
      </AppLayout>
    );
  }

  if (error && !task) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Task not found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/tasks')}
            className="text-flowky-primary hover:underline"
          >
            Back to tasks
          </button>
        </div>
      </AppLayout>
    );
  }

  const filteredProjects = formData.clientId
    ? projects.filter((p) => p.clientId === formData.clientId)
    : projects;

  return (
    <AppLayout>
      <Breadcrumb
        items={[
          { label: 'Tasks', href: '/tasks' },
          { label: task?.title || 'Edit', href: `/tasks/${id}` },
          { label: 'Edit' },
        ]}
      />

      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Task</h1>
        <p className="text-gray-600 mb-8">Update task information</p>

        {error && (
          <div className="border border-border rounded-lg p-4 p-4 mb-6 border-l-4 border-red-500">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Error</h3>
                <p className="text-gray-600 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="border border-border rounded-lg p-4 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Task Title *
            </label>
            <input
              id="title"
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="Design homepage mockups"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="Additional details about this task..."
              disabled={submitting}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
                Client
              </label>
              <select
                id="clientId"
                value={formData.clientId}
                onChange={(e) =>
                  setFormData({ ...formData, clientId: e.target.value, projectId: '' })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                disabled={submitting}
              >
                <option value="">No client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-1">
                Project
              </label>
              <select
                id="projectId"
                value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                disabled={submitting}
              >
                <option value="">No project</option>
                {filteredProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                Priority *
              </label>
              <select
                id="priority"
                required
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                disabled={submitting}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
                  Deadline
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setHasDeadline(!hasDeadline);
                    if (hasDeadline) {
                      setFormData({ ...formData, deadline: '' });
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    hasDeadline ? 'bg-flowky-primary' : 'bg-gray-300'
                  }`}
                  disabled={submitting}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      hasDeadline ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {hasDeadline && (
                <input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                  disabled={submitting}
                />
              )}
              {!hasDeadline && (
                <div className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-400 text-sm">
                  No deadline
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={submitting}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
